import { mergeSlotProps, useThemeProps } from '@mui/material';
import {
  default as MuiDialog,
  DialogProps as MuiDialogProps,
} from '@mui/material/Dialog';
import { ModalTypeMap as MuiModalTypeMap } from '@mui/material/Modal';
import { OverrideProps } from '@mui/material/OverridableComponent';
import useId from '@mui/utils/useId';
import * as React from 'react';
import {
  IUnifyDialogContext,
  UnifyDialogContextProvider,
} from 'src/components/Dialog/UnifyDialogContext';
import {
  ExtendTypeMap,
  forwardRefToOverridableComponent,
} from 'src/utils/forwardRefToOverridableComponent';
import UnifyThemeProvider from 'src/components/UnifyThemeProvider/UnifyThemeProvider';
import { useLegacyWsdStyles } from 'src/wsd/styles/context/useLegacyWsdStyles';

export interface DialogOwnProps {
  // Override this prop to be optional because it is often set by trigger
  // components and MUI doesn't throw if it is not set.
  /**
   * If `true`, the component is shown.
   *
   * Default: `false`
   */
  open?: boolean;

  /**
   * If `true`, a close button will be displayed in the title.
   *
   * Default: `false`
   */
  showCloseButton?: boolean;
}

export type DialogTypeMap<
  AdditionalProps = unknown,
  RootComponent extends React.ElementType = MuiModalTypeMap['defaultComponent']
> = ExtendTypeMap<
  {
    props: AdditionalProps & Omit<MuiDialogProps, 'open'> & DialogOwnProps;
    defaultComponent: MuiModalTypeMap['defaultComponent'];
  },
  AdditionalProps,
  RootComponent
>;

export type DialogProps<
  RootComponent extends React.ElementType = MuiModalTypeMap['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<DialogTypeMap<AdditionalProps, RootComponent>, RootComponent>;

/**
 * Dialogs are overlaid modal paper based components with a backdrop.
 *
 * Demos:
 *
 * - [Dialog](https://mui.com/material-ui/react-dialog/)
 *
 * API:
 *
 * - [Dialog API](https://mui.com/material-ui/api/dialog/)
 * - inherits [Modal API](https://mui.com/material-ui/api/modal/)
 */
export const Dialog = forwardRefToOverridableComponent<
  DialogTypeMap,
  DialogProps
>(function Dialog(inProps, forwardedRef) {
  const legacyWsdStyleSupport = useLegacyWsdStyles();
  const props = useThemeProps({ props: inProps, name: 'MuiDialog' });
  const {
    children,
    open: openProp,
    slotProps,
    onClose,
    // eslint-disable-next-line deprecation/deprecation
    PaperProps: PaperPropsProp = {},
    showCloseButton = false,
    ...rest
  } = props;
  const dialogId = useId(PaperPropsProp.id);
  const unifyDialogContextValue: IUnifyDialogContext = React.useMemo(() => {
    return {
      dialogId,
      showCloseButton,
      onClose,
    };
  }, [dialogId, showCloseButton, onClose]);
  const PaperProps = {
    ...PaperPropsProp,
    id: dialogId,
  };

  const dialog = (
    <MuiDialog
      open={openProp ?? false}
      onClose={onClose}
      ref={forwardedRef}
      {...rest}
      slotProps={{
        ...slotProps,
        paper: mergeSlotProps(slotProps?.paper, PaperProps),
      }}
    >
      <UnifyDialogContextProvider value={unifyDialogContextValue}>
        {children}
      </UnifyDialogContextProvider>
    </MuiDialog>
  );

  if (!legacyWsdStyleSupport) {
    // unifyStyleContext.legacyWsdStyleSupport is false:
    //   No chance of legacy styling polluting the components in the Dialog.
    return dialog;
  } else {
    // unifyStyleContext.legacyWsdStyleSupport is true:
    //   Wrap the Dialog in a UnifyThemeProvider
    //   to avoid legacy styling polluting the components in the Dialog.
    return (
      <UnifyThemeProvider excludeScopedCssBaseline>{dialog}</UnifyThemeProvider>
    );
  }
});

export default Dialog;
